import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useApi } from '../../app/providers';
import type { Locale, Org, Profile } from '../../lib/api';
import { Button, Checkbox, Input, linkClass, RadioGroup, Select } from '../../ui';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { newPasswordSchema } from '../auth/schemas';
import { useSession } from '../auth/session';
import { Section } from '../dashboard/cards';
import { qk, useOrg } from '../dashboard/queries';
import { PendingInvitations } from '../sharing/PendingInvitations';
import { InviteForm } from '../sharing/PeopleTab';
import { href, Link } from '../../app/router';
import { PageSkeleton } from '../../ui/states';
import { Icon } from '../../ui/icons';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useTheme } from '../../app/theme-store';
import { cn } from '../../ui/cn';
import { focusRing } from '../../ui/focus';
import { Kbd } from '../../ui';
import { EditorShortcutsTable, formatCombo } from '../editor/ShortcutsHelp';
import { DASHBOARD_SHORTCUTS } from '../dashboard/useDashboardKeys';
import { formatBytes } from './format';
import { StorageSection } from './StorageSection';
import { QuotasSection } from './QuotasSection';
import { AvatarPicker } from './AvatarPicker';
import { EmailSection } from './EmailSection';

const sectionLink = cn(
  'flex rounded-md px-2.5 py-1.5 font-sans text-sm text-fg-muted hover:bg-surface-2 hover:text-fg aria-[current=page]:bg-surface-2 aria-[current=page]:font-medium aria-[current=page]:text-fg pointer-coarse:min-h-11',
  focusRing,
);

const colorSchema = z.union([z.literal(''), z.string().regex(/^#[0-9a-f]{6}$/i)]);

/** `hasOrg`: only a studio member has a photo (P5g: its key lives under the organization). */
function ProfileForm({ profile, hasOrg }: { profile: Profile; hasOrg: boolean }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(profile.displayName);
  const locale = profile.locale;
  const [color, setColor] = useState(profile.color ?? '');
  const [commentDigest, setCommentDigest] = useState(profile.commentDigest);
  const session = useSession();
  // The digest goes to a confirmed email address: anonymous guests have none.
  const canDigest = session.status === 'signed-in' && !session.user.isAnonymous && !!session.user.email;
  const canPhoto = api.capabilities.avatars && hasOrg && session.status === 'signed-in' && !session.user.isAnonymous;
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const dirty =
    displayName !== profile.displayName || locale !== profile.locale || color !== (profile.color ?? '') || (canDigest && commentDigest !== profile.commentDigest);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const name = z.string().trim().max(80).safeParse(displayName);
    const col = colorSchema.safeParse(color.trim().toLowerCase());
    if (!name.success) return setMessage({ ok: false, text: t('dashboard.nameLength', { max: 80 }) });
    if (!col.success) return setMessage({ ok: false, text: t('settings.invalidColor') });
    setSaving(true);
    try {
      await api.updateProfile({ displayName: name.data, locale, color: col.data || null, ...(canDigest && { commentDigest }) });
      await queryClient.invalidateQueries({ queryKey: qk.profile });
      // Only an explicit change of the field switches the UI language.
      if (locale !== profile.locale) await i18n.changeLanguage(locale);
      setMessage({ ok: true, text: t('settings.saved') });
    } catch {
      setMessage({ ok: false, text: t('auth.genericError') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title={t('settings.profile')}>
      {canPhoto && (
        <div className="mb-4 max-w-sm">
          <AvatarPicker profile={profile} />
        </div>
      )}
      <form className="flex max-w-sm flex-col gap-4" onSubmit={submit} noValidate>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('settings.displayName')}
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('settings.color')}
          <Input value={color} placeholder="#d90429" onChange={(e) => setColor(e.target.value)} />
        </label>
        {api.capabilities.comments && (
          <div className="flex flex-col gap-1">
            <Checkbox label={t('comments.digest')} checked={commentDigest && canDigest} disabled={!canDigest} onCheckedChange={setCommentDigest} />
            <span className="font-sans text-xs text-fg-muted">{canDigest ? t('comments.digestHint') : t('comments.digestUnavailable')}</span>
          </div>
        )}
        {message && (
          <p role={message.ok ? 'status' : 'alert'} className={message.ok ? 'font-sans text-sm text-fg-muted' : 'font-sans text-xs text-danger'}>
            {message.text}
          </p>
        )}
        <Button variant="primary" type="submit" disabled={!dirty || saving} className="self-start">
          {saving ? t('settings.saving') : t('settings.saveProfile')}
        </Button>
      </form>
    </Section>
  );
}

function PasswordForm() {
  const { t } = useTranslation();
  const api = useApi();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!newPasswordSchema.safeParse(password).success) return setMessage({ ok: false, text: t('auth.passwordTooShort') });
    if (password !== confirm) return setMessage({ ok: false, text: t('settings.passwordMismatch') });
    try {
      await api.auth.updatePassword(password);
      setPassword('');
      setConfirm('');
      setMessage({ ok: true, text: t('auth.passwordSaved') });
    } catch {
      setMessage({ ok: false, text: t('auth.genericError') });
    }
  };

  return (
    <Section title={t('settings.password')}>
      <form className="flex max-w-sm flex-col gap-4" onSubmit={submit} noValidate>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('auth.newPassword')}
          <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('settings.confirmPassword')}
          <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </label>
        {message && (
          <p role={message.ok ? 'status' : 'alert'} className={message.ok ? 'font-sans text-sm text-fg-muted' : 'font-sans text-xs text-danger'}>
            {message.text}
          </p>
        )}
        <Button type="submit">
          {t('settings.changePassword')}
        </Button>
      </form>
    </Section>
  );
}

/**
 * Who is in the studio, and invitations (P17): people come in through an invitation link, never by adding an existing
 * account. The link is always shown, as no email may have left; admins and the owner only (the section is theirs).
 */
function MembersPanel({ org }: { org: Org }) {
  const { t } = useTranslation();
  const api = useApi();
  const members = useQuery({ queryKey: qk.members(org.id), queryFn: () => api.listMembers(org.id) });
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: qk.members(org.id) });
  const remove = useMutation({ mutationFn: (userId: string) => api.removeMember(org.id, userId), onSettled: refresh });
  // Who is asked about stays set while the dialog leaves, so its text holds under the exit animation.
  const [removing, setRemoving] = useState<{ userId: string; displayName: string } | null>(null);
  const [asking, setAsking] = useState(false);
  const scope = { type: 'org', id: org.id } as const;

  return (
    <Section title={t('settings.members')}>
      <ul className="flex max-w-lg flex-col gap-2">
        {members.data?.map((m) => (
          <li key={m.userId} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
            <span className="flex flex-col">
              <span className="font-sans text-sm">{m.displayName}</span>
              <span className="font-mono text-xs text-fg-muted">{t(`settings.role.${m.role}`)}</span>
            </span>
            {m.role !== 'owner' && (
              <Button
                onClick={() => {
                  setRemoving(m);
                  setAsking(true);
                }}
              >
                {t('settings.remove')}
              </Button>
            )}
          </li>
        ))}
      </ul>
      {api.capabilities.sharing && (
        <div className="mt-2 flex max-w-lg flex-col gap-3">
          <h3 className="font-sans text-sm font-semibold">{t('sharing.inviteOrg')}</h3>
          <p className="font-sans text-xs text-fg-muted">{t('sharing.inviteOrgHint')}</p>
          <InviteForm scope={scope} roles={['member', 'admin']} defaultRole="member" flags={false} />
          <PendingInvitations scope={scope} />
        </div>
      )}
      {removing && (
        <ConfirmDialog
          open={asking}
          destructive
          title={t('p18.e.removeMemberTitle', { name: removing.displayName })}
          body={t('p18.e.removeMemberBody')}
          confirmLabel={t('p18.e.removeMemberConfirm')}
          cancelLabel={t('p18.e.cancel')}
          onConfirm={() =>
            remove
              .mutateAsync(removing.userId)
              .catch(() => undefined)
              .finally(() => setAsking(false))
          }
          onCancel={() => setAsking(false)}
        />
      )}
    </Section>
  );
}

const THEME_OPTIONS = [
  { value: 'system', key: 'settings.themeSystem', icon: Monitor },
  { value: 'light', key: 'settings.themeLight', icon: Sun },
  { value: 'dark', key: 'settings.themeDark', icon: Moon },
] as const;

/**
 * Appearance and language (P16-4): the theme is a device preference (Ruling 11 of P11), the language goes with the
 * profile; both apply at once, no save button.
 */
function AppearancePanel() {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const [preference, setPreference] = useTheme();
  const language = async (locale: Locale) => {
    await i18n.changeLanguage(locale);
    await api.updateProfile({ locale }).catch(() => {});
    await queryClient.invalidateQueries({ queryKey: qk.profile });
  };
  return (
    <Section title={t('settings.sections.appearance')}>
      <label className="flex max-w-sm flex-col gap-1 font-sans text-sm font-medium text-fg">
        {t('settings.language')}
        <Select value={i18n.language.startsWith('fr') ? 'fr' : 'en'} onChange={(e) => void language(e.target.value as Locale)}>
          <option value="fr">Français</option>
          <option value="en">English</option>
        </Select>
      </label>
      <div className="flex flex-col gap-2">
        <RadioGroup
          variant="segmented"
          label={t('settings.appearance')}
          value={preference}
          onValueChange={setPreference}
          options={THEME_OPTIONS.map((option) => ({ value: option.value, label: t(option.key), icon: <Icon icon={option.icon} size={16} /> }))}
          className="w-fit"
        />
        <p className="font-sans text-xs text-fg-muted">{t('settings.appearanceHint')}</p>
      </div>
    </Section>
  );
}

const ROLE_KEY = { owner: 'settings.roles.owner', admin: 'settings.roles.admin', member: 'settings.roles.member' } as const;

/** The organization as the person sees it: its name, their role, the storage quota (renaming needs an RPC: later). */
function OrganizationPanel({ org }: { org: Org }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const storage = useQuery({ queryKey: ['storage', org.id], queryFn: () => api.orgStorage(org.id) });
  const row = 'flex flex-col gap-0.5 font-sans text-sm';
  return (
    <Section title={t('settings.sections.organization')}>
      <dl className="flex flex-col gap-3">
        <div className={row}>
          <dt className="text-fg-muted">{t('settings.orgName')}</dt>
          <dd className="text-fg">{org.name}</dd>
        </div>
        <div className={row}>
          <dt className="text-fg-muted">{t('settings.yourRole')}</dt>
          <dd className="text-fg">{t(ROLE_KEY[org.role])}</dd>
        </div>
        {storage.data && (
          <div className={row}>
            <dt className="text-fg-muted">{t('settings.quota')}</dt>
            <dd className="text-fg">{formatBytes(storage.data.quotaBytes, i18n.language)}</dd>
          </div>
        )}
      </dl>
    </Section>
  );
}

/** Dashboard and editor shortcuts in one place (P16-4). */
function ShortcutsPanel() {
  const { t } = useTranslation();
  return (
    <Section title={t('settings.sections.shortcuts')}>
      <h3 className="font-sans text-sm font-semibold text-fg">{t('settings.shortcutsDashboard')}</h3>
      <table className="w-full font-sans text-sm">
        <tbody>
          {DASHBOARD_SHORTCUTS.map(({ combo, key }) => (
            <tr key={combo} className="border-t border-border">
              <td className="py-1.5 pr-4">
                <Kbd>{formatCombo(combo)}</Kbd>
              </td>
              <td className="py-1.5">{t(key)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3 className="mt-4 font-sans text-sm font-semibold text-fg">{t('settings.shortcutsEditor')}</h3>
      <EditorShortcutsTable />
    </Section>
  );
}

/** A section of the settings (P16-4 Ruling 9): who sees it. */
export const SETTINGS_SECTIONS = [
  { id: 'profile', key: 'settings.sections.profile', org: false, admin: false },
  { id: 'organization', key: 'settings.sections.organization', org: true, admin: false },
  { id: 'members', key: 'settings.sections.members', org: true, admin: true },
  { id: 'storage', key: 'settings.sections.storage', org: true, admin: false },
  { id: 'quotas', key: 'settings.sections.quotas', org: true, admin: true },
  { id: 'appearance', key: 'settings.sections.appearance', org: false, admin: false },
  { id: 'shortcuts', key: 'settings.sections.shortcuts', org: false, admin: false },
  { id: 'advanced', key: 'settings.sections.advanced', org: true, admin: true },
] as const;
export type SettingsSectionId = (typeof SETTINGS_SECTIONS)[number]['id'];

export function SettingsPage({ section }: { section?: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const profile = useQuery({ queryKey: qk.profile, queryFn: () => api.getProfile() });
  const { org } = useOrg();
  const admin = Boolean(org && org.role !== 'member');
  const visible = SETTINGS_SECTIONS.filter((s) => (!s.org || org) && (!s.admin || admin) && (s.id !== 'advanced' || api.capabilities.sharing));
  const current = visible.find((s) => s.id === section)?.id ?? 'profile';

  const content: Record<SettingsSectionId, () => ReactNode> = {
    profile: () => (
      <>
        {profile.data ? <ProfileForm key={profile.data.locale} profile={profile.data} hasOrg={!!org} /> : <PageSkeleton label={t('auth.loading')} />}
        <EmailSection />
        <PasswordForm />
      </>
    ),
    organization: () => org && <OrganizationPanel org={org} />,
    members: () => org && <MembersPanel org={org} />,
    storage: () => org && <StorageSection org={org} />,
    quotas: () => org && <QuotasSection org={org} />,
    appearance: () => <AppearancePanel />,
    shortcuts: () => <ShortcutsPanel />,
    advanced: () => (
      <Section title={t('settings.sections.advanced')}>
        <Link to={href('audit')} className={linkClass}>
          {t('audit.link')}
        </Link>
      </Section>
    ),
  };

  return (
    <>
      <h1 className="font-display text-display-md font-normal tracking-display text-fg">{t('dashboard.settings')}</h1>
      <div className="flex flex-col gap-8 md:flex-row">
        {/* Phone: the list alone at /settings, the section alone once chosen (with the list a tap away). */}
        <nav aria-label={t('settings.sectionsLabel')} className={cn('shrink-0 md:w-52', section && 'max-md:hidden')}>
          <ul className="flex flex-col gap-0.5">
            {visible.map((s) => (
              <li key={s.id}>
                <Link to={href('settingsSection', { section: s.id })} aria-current={s.id === current ? 'page' : undefined} className={sectionLink}>
                  {t(s.key)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className={cn('flex min-w-0 flex-1 flex-col gap-10', !section && 'max-md:hidden')}>
          {section && (
            <Link to={href('settings')} className={cn(linkClass, 'flex items-center self-start pointer-coarse:min-h-11 md:hidden')}>
              ← {t('settings.allSections')}
            </Link>
          )}
          {content[current]()}
        </div>
      </div>
    </>
  );
}
